import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import {
  Users, UserCheck, UserPlus, Plane, Calendar, Briefcase,
  GraduationCap, ArrowUpRight, TrendingUp
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis,
  Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend
} from "recharts";

const COLORS = ["#F26B3A", "#1E88E5", "#f59e0b", "#3b82f6", "#8b5cf6", "#ef4444"];

function Kpi({ label, value, icon: Icon, hint, testId }) {
  return (
    <Card data-testid={testId} className="hr-card p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-gray-500">{label}</div>
          <div className="hr-kpi mt-1.5">{value}</div>
          {hint && <div className="text-xs text-gray-500 mt-1">{hint}</div>}
        </div>
        <div className="h-10 w-10 rounded-lg bg-[#F26B3A]/5 text-[#0F172A] grid place-items-center">
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [report, setReport] = useState(null);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      api.get("/dashboard/stats"),
      api.get("/dashboard/activity"),
      api.get("/dashboard/announcements"),
      api.get("/reports/summary"),
    ]).then(([s, a, an, r]) => {
      if (!mounted) return;
      setStats(s.data);
      setActivity(a.data);
      setAnnouncements(an.data);
      setReport(r.data);
    });
    return () => { mounted = false; };
  }, []);

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        testId="dashboard-header"
        title={`Hello, ${user?.name?.split(" ")[0] || "there"}.`}
        subtitle="Here's what's happening across your organization today."
      />

      {!stats ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {Array.from({length: 8}).map((_, i) => <Skeleton key={`kpi-skel-${i}`} className="h-28 rounded-lg" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Kpi testId="kpi-total-employees" label="Total Employees" value={stats.total_employees} icon={Users} hint={`${stats.active_employees} active`} />
          <Kpi testId="kpi-new-joiners" label="New Joiners" value={stats.new_joiners} icon={UserPlus} hint="In probation" />
          <Kpi testId="kpi-pending-approvals" label="Pending Approvals" value={stats.pending_approvals} icon={UserCheck} hint="Leaves awaiting action" />
          <Kpi testId="kpi-on-leave" label="On Leave Today" value={stats.employees_on_leave} icon={Plane} hint={`${stats.attendance_percent}% attendance`} />
          <Kpi testId="kpi-open-jobs" label="Open Jobs" value={stats.open_jobs} icon={Briefcase} hint="Active requisitions" />
          <Kpi testId="kpi-onboarding" label="Onboarding" value={stats.pending_onboarding} icon={UserPlus} hint="In progress" />
          <Kpi testId="kpi-trainings" label="Upcoming Trainings" value={stats.upcoming_trainings} icon={GraduationCap} hint="Scheduled" />
          <Kpi testId="kpi-attendance" label="Attendance" value={`${stats.attendance_percent}%`} icon={Calendar} hint="Last 7 days" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card className="hr-card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-display text-lg font-semibold">Headcount by department</h2>
              <p className="text-xs text-gray-500">Live snapshot</p>
            </div>
            <TrendingUp className="h-4 w-4 text-[#1E88E5]" />
          </div>
          <div className="h-64">
            {report && (
              <ResponsiveContainer>
                <BarChart data={(() => {
                  const allowed = ["Medical Coding", "Human Resources", "IT Operations", "Finance"];
                  const map = Object.fromEntries((report.headcount_by_dept || []).map((d) => [d.name, d.value]));
                  return allowed.map((name) => ({ name, value: map[name] || 0 }));
                })()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis dataKey="name" stroke="#6b7280" tick={{ fontSize: 11 }} />
                  <YAxis stroke="#6b7280" tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#F26B3A" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card className="hr-card p-5">
          <h2 className="font-display text-lg font-semibold mb-1">Recruitment pipeline</h2>
          <p className="text-xs text-gray-500 mb-3">Candidates by stage</p>
          <div className="h-64">
            {report && (
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={report.candidates_by_stage} dataKey="value" nameKey="name" outerRadius={80} innerRadius={45}>
                    {report.candidates_by_stage.map((entry) => (
                      <Cell key={`cell-${entry.name}`} fill={COLORS[report.candidates_by_stage.indexOf(entry) % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="hr-card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-lg font-semibold">Recent activity</h2>
            <ArrowUpRight className="h-4 w-4 text-gray-400" />
          </div>
          <ul data-testid="activity-list" className="divide-y divide-gray-100">
            {activity.length === 0 && (
              <li className="text-sm text-gray-500 py-4">Nothing yet &mdash; you&apos;re all caught up.</li>
            )}
            {activity.map((a) => (
              <li key={`${a.type}-${a.at}-${a.title}`} className="py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm text-gray-800 truncate">{a.title}</div>
                  <div className="text-[11px] text-gray-500">{new Date(a.at).toLocaleString()}</div>
                </div>
                <StatusBadge status={a.status} />
              </li>
            ))}
          </ul>
        </Card>

        <Card className="hr-card p-5">
          <h2 className="font-display text-lg font-semibold mb-3">Announcements</h2>
          <ul data-testid="announcements-list" className="space-y-3">
            {announcements.length === 0 && <li className="text-sm text-gray-500">No announcements.</li>}
            {announcements.map((a) => (
              <li key={a.id} className="border-l-2 border-[#F26B3A] pl-3">
                <div className="text-sm font-medium">{a.title}</div>
                <div className="text-xs text-gray-500 mt-0.5">{a.body}</div>
                <div className="text-[10px] uppercase tracking-wider text-gray-400 mt-1">{a.author}</div>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}
